import { formatAmount, formatMinorAmount, formatPoints, formatPointsRate } from '../utils';
import { DgcOrder } from 'api/types/dgc/dgcOrder';
import { DgcActivationLink, DgcOrderCard, DgcOrderDetail } from 'api/types/dgc/dgcOrderDetail';
import { ALL } from 'shared/consts/dgcOrders';
import { SortDirection } from 'shared/types/dgcOrders';

export { ALL };

export const OUTER_CONTAINER_CLASSES = 'w-full max-w-full flex flex-none items-center px-4 md:px-10';
export const INNER_CONTAINER_CLASSES = 'flex flex-none w-full max-w-[1360px]';

export const HEADER_ROW_STYLES = 'flex pt-12 pb-3.5 px-10 gap-2 items-end';
export const HEADER_CELL_STYLES = 'text-left uppercase font-klarheit tracking-tight text-base font-semibold';
export const DATA_ROW_STYLES =
  'flex py-5 font-normal gap-2 items-center px-10 hover:cursor-pointer hover:bg-palette-secondary-light-grey';
export const DATA_CELL_STYLES = 'text-left text-sm truncate break-all tracking-tight';
// Keeps columns legible once a page has many of them; the grid scrolls horizontally instead of crushing cells.
// The max width stops a long value (e.g. an auth0 customer id) from stretching one column across the viewport.
export const COLUMN_SIZE_STYLES = 'flex-1 min-w-[100px] max-w-[220px]';
export const FOOTER_ROW_STYLES =
  'flex flex-col gap-4 py-8 px-4 border-t border-palette-color-bg-grey sm:flex-row sm:gap-2 sm:justify-between sm:py-12 sm:px-10';

export const SEARCH_DEBOUNCE_TIME = 800;

export const FILTER_KEYS = {
  pageNum: 'pageNum',
  status: 'status',
  deliveryMethod: 'deliveryMethod',
  paymentType: 'paymentType',
  clientName: 'clientName',
  orderRef: 'orderRef',
  createdDateFrom: 'createdDateFrom',
  createdDateTo: 'createdDateTo',
  sortBy: 'sortBy',
  sortDir: 'sortDir',
};

export const DEFAULT_SORT_BY = 'created_at';
export const DEFAULT_SORT_DIR: SortDirection = 'DESC';

export interface FilterOption {
  value: string;
  label: string;
}

// OMS exposes no statuses endpoint, so this mirrors its OrderStatus enum.
export const STATUS_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Statuses' },
  { value: 'PENDING', label: 'PENDING' },
  { value: 'PROCESSING', label: 'PROCESSING' },
  { value: 'PAYMENT_FAILED', label: 'PAYMENT FAILED' },
  { value: 'PAID', label: 'PAID' },
  { value: 'FAILED', label: 'FAILED' },
  { value: 'COMPLETED', label: 'COMPLETED' },
  { value: 'CANCELLED', label: 'CANCELLED' },
];

// Delivery and payment options differ by card type: SINGLE/BULK and CASH/VISA/POINTS are
// digital-only, the rest are the methods OMS sends to Premo for physical orders.
export const DIGITAL_DELIVERY_METHOD_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Delivery Methods' },
  { value: 'SINGLE', label: 'Single' },
  { value: 'BULK', label: 'Multiple' },
];

export const PHYSICAL_DELIVERY_METHOD_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Delivery Methods' },
  { value: 'PICK_UP', label: 'Click & Collect' },
  { value: 'COURIER', label: 'Home Delivery' },
];

export const DIGITAL_PAYMENT_TYPE_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Payment Types' },
  { value: 'CASH', label: 'Cash' },
  { value: 'VISA', label: 'Visa' },
  { value: 'POINTS', label: 'Points' },
];

export const PHYSICAL_PAYMENT_TYPE_OPTIONS: FilterOption[] = [
  { value: ALL, label: 'All Payment Types' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'NAJM', label: 'Najm' },
  { value: 'COD', label: 'Cash on Delivery' },
  { value: 'UAEPGS', label: 'UAEPGS' },
];

const ENUM_VALUE_LABELS: Record<string, string> = Object.fromEntries(
  [
    ...DIGITAL_DELIVERY_METHOD_OPTIONS,
    ...PHYSICAL_DELIVERY_METHOD_OPTIONS,
    ...DIGITAL_PAYMENT_TYPE_OPTIONS,
    ...PHYSICAL_PAYMENT_TYPE_OPTIONS,
  ]
    .filter((option) => option.value !== ALL)
    .map((option) => [option.value, option.label])
);

export function humanizeEnumValue(value: string): string {
  return ENUM_VALUE_LABELS[value] ?? value;
}

// Mirrors DigitalGiftCards' USER_FILTERS: one text search field visible at a time, picked by radio button.
export const TEXT_FILTERS = [
  { label: 'Order Ref', id: FILTER_KEYS.orderRef },
  { label: 'Client Name', id: FILTER_KEYS.clientName },
];

export interface TableColumn {
  headerLabel: string;
  key: keyof DgcOrder;
  // OMS can order by this column (its OrderSortField enum); every other header stays inert.
  sortable?: boolean;
  // Identifiers operators paste into other systems (payment gateway, Premo, invoicing), so they get a copy control.
  copyable?: boolean;
  // How the raw value is rendered; omitted means print it as-is.
  format?: 'date' | 'amount' | 'enum';
  // Reference columns must stay fully readable: wider, and wrapping instead of truncating.
  wrap?: boolean;
}

export const WRAPPED_COLUMN_SIZE_STYLES = 'flex-1 min-w-[180px] max-w-[320px] !whitespace-normal !overflow-visible';

// One definition per column so the two card types share traits instead of restating them.
const COLUMNS = {
  orderRef: { headerLabel: 'Order Ref', key: 'order_ref', sortable: true, copyable: true, wrap: true },
  invoiceRef: { headerLabel: 'Invoice Ref', key: 'invoice_ref', copyable: true, wrap: true },
  client: { headerLabel: 'Client', key: 'client_name' },
  customer: { headerLabel: 'Customer', key: 'customer_id', copyable: true },
  source: { headerLabel: 'Source', key: 'source' },
  status: { headerLabel: 'Status', key: 'status' },
  cardType: { headerLabel: 'Card Type', key: 'card_type' },
  deliveryMethod: { headerLabel: 'Delivery', key: 'delivery_method', format: 'enum' },
  deliveryFee: { headerLabel: 'Delivery Fee', key: 'delivery_fee_amount', format: 'amount' },
  total: { headerLabel: 'Total', key: 'total_amount', format: 'amount' },
  paymentType: { headerLabel: 'Payment', key: 'payment_type', format: 'enum' },
  paymentId: { headerLabel: 'Payment ID', key: 'payment_id', copyable: true },
  issuer: { headerLabel: 'Issuer', key: 'issuance_driver' },
  quantity: { headerLabel: 'Quantity', key: 'number_of_cards' },
  createdAt: { headerLabel: 'Created At', key: 'created_at', sortable: true, format: 'date' },
  updatedAt: { headerLabel: 'Updated At', key: 'updated_at', format: 'date' },
} satisfies Record<string, TableColumn>;

export const TABLE_COLUMN_MAP: TableColumn[] = [
  COLUMNS.orderRef,
  COLUMNS.invoiceRef,
  COLUMNS.client,
  COLUMNS.source,
  COLUMNS.status,
  COLUMNS.cardType,
  COLUMNS.quantity,
  COLUMNS.createdAt,
  COLUMNS.updatedAt,
];

export const PHYSICAL_TABLE_COLUMN_MAP: TableColumn[] = [
  COLUMNS.orderRef,
  COLUMNS.invoiceRef,
  COLUMNS.client,
  COLUMNS.customer,
  COLUMNS.status,
  COLUMNS.deliveryMethod,
  COLUMNS.deliveryFee,
  COLUMNS.total,
  COLUMNS.paymentType,
  COLUMNS.paymentId,
  COLUMNS.issuer,
  COLUMNS.quantity,
  COLUMNS.createdAt,
  COLUMNS.updatedAt,
];

export const ACTIONS_COLUMN_STYLES = 'w-24 shrink-0 text-right';

// Mirrors OMS's CANCELLABLE_STATUSES in order-cancellation.service.ts.
export const CANCELLABLE_STATUSES = ['PENDING', 'COMPLETED'];

export const NOT_CANCELLABLE_TOOLTIP_COPY: Record<string, string> = {
  CANCELLED: 'This order has already been cancelled.',
  PROCESSING: 'Orders that are still processing payment cannot be cancelled.',
  PAYMENT_FAILED: 'This order failed at payment and cannot be cancelled.',
  PAID: 'Orders that are still issuing cards cannot be cancelled.',
  FAILED: 'This order failed and cannot be cancelled.',
};

export interface DetailInfoRow<T> {
  headerLabel: string;
  getValue: (entity: T) => string;
  // Identifiers operators paste elsewhere; the copy control is suppressed automatically when the value is missing.
  copyable?: boolean;
}

export interface DetailInfoGroup {
  title: string;
  rows: DetailInfoRow<DgcOrderDetail>[];
  columns?: 2 | 3;
}

const CUSTOMER_ROWS: DetailInfoRow<DgcOrderDetail>[] = [
  {
    headerLabel: 'Initiator',
    getValue: (order) => [order.initiator_first_name, order.initiator_last_name].filter(Boolean).join(' ') || '—',
  },
  { headerLabel: 'Email', getValue: (order) => order.initiator_email ?? '—' },
  { headerLabel: 'Mobile', getValue: (order) => order.initiator_mobile ?? '—' },
  { headerLabel: 'Customer ID', getValue: (order) => order.customer_id ?? '—', copyable: true },
];

const PAYMENT_ROWS: DetailInfoRow<DgcOrderDetail>[] = [
  { headerLabel: 'Method', getValue: (order) => humanizeEnumValue(order.payment_type ?? '—') },
  { headerLabel: 'MMA', getValue: (order) => order.mma ?? '—', copyable: true },
];

const POINTS_ROWS: DetailInfoRow<DgcOrderDetail>[] = [
  { headerLabel: 'Points used', getValue: (order) => formatPoints(order.points_amount) },
  {
    headerLabel: 'Points value',
    getValue: (order) => formatMinorAmount(order.points_value_minor, order.currency),
  },
  { headerLabel: 'Points rate', getValue: (order) => formatPointsRate(order.points_rate, order.currency) },
];

const PAYMENT_ID_ROW: DetailInfoRow<DgcOrderDetail> = {
  headerLabel: 'Payment ID',
  getValue: (order) => order.payment_id ?? '—',
  copyable: true,
};

const DELIVERY_ROW: DetailInfoRow<DgcOrderDetail> = {
  headerLabel: 'Method',
  getValue: (order) => humanizeEnumValue(order.delivery_method),
};

const ISSUER_ROW: DetailInfoRow<DgcOrderDetail> = {
  headerLabel: 'Issuer',
  getValue: (order) => order.issuance_driver ?? '—',
};

export const DETAIL_GENERAL_INFO_GROUPS: DetailInfoGroup[] = [
  { title: 'Customer', rows: CUSTOMER_ROWS },
  { title: 'Payment', rows: PAYMENT_ROWS },
  { title: 'Points', rows: POINTS_ROWS, columns: 3 },
  { title: 'Delivery', rows: [DELIVERY_ROW] },
];

export const DETAIL_PHYSICAL_GENERAL_INFO_GROUPS: DetailInfoGroup[] = [
  { title: 'Customer', rows: CUSTOMER_ROWS },
  { title: 'Payment', rows: [...PAYMENT_ROWS, PAYMENT_ID_ROW] },
  { title: 'Points', rows: POINTS_ROWS, columns: 3 },
  { title: 'Delivery', rows: [DELIVERY_ROW, ISSUER_ROW] },
];

// Charges render as their own right-aligned block so the figures line up and sum visibly to the total.
// Caps + tracking, not size, is what separates a section heading from a field label: both are small and muted.
export const DETAIL_SECTION_HEADING_STYLES =
  'font-klarheit text-xs font-bold uppercase tracking-[0.08em] text-palette-secondary-grey';

export const DETAIL_CHARGE_ROWS: DetailInfoRow<DgcOrderDetail>[] = [
  { headerLabel: 'Subtotal', getValue: (order) => formatAmount(order.charges.subtotal, order.currency) },
  { headerLabel: 'Delivery fee', getValue: (order) => formatAmount(order.charges.delivery_fee, order.currency) },
  { headerLabel: 'Activation fee', getValue: (order) => formatAmount(order.charges.activation_fee, order.currency) },
];

export const DETAIL_TOTAL_ROW: DetailInfoRow<DgcOrderDetail> = {
  headerLabel: 'Total',
  getValue: (order) => formatAmount(order.charges.total, order.currency),
};

export interface DetailCard {
  card: DgcOrderCard;
  link: DgcActivationLink;
}

export const DETAIL_CARD_INFO_MAP: DetailInfoRow<DetailCard>[] = [
  { headerLabel: 'Activation Link', getValue: ({ link }) => link.url, copyable: true },
  { headerLabel: 'I2C Card Reference ID', getValue: ({ link }) => link.card_reference_id ?? '—', copyable: true },
  { headerLabel: 'Recipient Name', getValue: ({ card }) => card.recipient_name },
  { headerLabel: 'Recipient Email', getValue: ({ card }) => card.recipient_email ?? '—' },
  { headerLabel: 'Recipient Mobile', getValue: ({ card }) => card.recipient_mobile ?? '—' },
];

// Recipient name, quantity and amount are shown on the collapsed card row, so they are not repeated here.
export const DETAIL_PHYSICAL_CARD_INFO_MAP: DetailInfoRow<DgcOrderCard>[] = [
  { headerLabel: 'Premo reference', getValue: (card) => card.premo_reference ?? '—', copyable: true },
  { headerLabel: 'Recipient email', getValue: (card) => card.recipient_email ?? '—' },
  { headerLabel: 'Recipient mobile', getValue: (card) => card.recipient_mobile ?? '—' },
  {
    headerLabel: 'Address',
    getValue: (card) =>
      [card.recipient_address1, card.recipient_address2, card.recipient_city].filter(Boolean).join(', ') || '—',
  },
];
